import {
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  forwardRef,
  Fragment,
  type KeyboardEvent,
  useImperativeHandle,
  useRef,
} from 'react';

import { StyledCell, StyledContainer, StyledDivider, StyledGroup } from './OtpInput.styled';
import type { OtpInputHandle, OtpInputProps } from './types';

const OtpInput = forwardRef<OtpInputHandle, OtpInputProps>((props, ref) => {
  const {
    length = 6,
    value = '',
    onChange,
    disabled = false,
    error = false,
    placeholder = '0',
    groupSize,
    fullWidth = false,
  } = props;
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  useImperativeHandle(
    ref,
    () => ({
      focusFirst: () => inputsRef.current[0]?.focus(),
    }),
    [],
  );
  const vals = Array.from({ length }, (_, i) => value[i] ?? '');

  const size = groupSize && groupSize > 0 ? groupSize : length;
  const groups: number[][] = [];
  for (let i = 0; i < length; i += size) {
    groups.push(Array.from({ length: Math.min(size, length - i) }, (_, j) => i + j));
  }

  const notifyChange = (newVals: string[]) => {
    onChange?.(newVals.join(''));
  };

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (digits.length > 1) {
      notifyChange(digits.slice(0, length).split(''));
      inputsRef.current[Math.min(digits.length, length - 1)]?.focus();
      return;
    }
    const digit = digits.slice(-1);
    const newVals = [...vals];
    newVals[index] = digit;
    notifyChange(newVals);
    if (digit && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !vals[index] && index > 0) {
      e.preventDefault();
      const newVals = [...vals];
      newVals[index - 1] = '';
      notifyChange(newVals);
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    const newVals = Array.from({ length }, (_, i) => pasted[i] ?? '');
    notifyChange(newVals);
    requestAnimationFrame(() => {
      inputsRef.current[Math.min(pasted.length, length - 1)]?.focus();
    });
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const renderCell = (i: number) => (
    <StyledCell
      key={i}
      ref={(el) => {
        inputsRef.current[i] = el;
      }}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="one-time-code"
      maxLength={length}
      value={vals[i]}
      placeholder={placeholder}
      onChange={(e) => handleChange(i, e)}
      onKeyDown={(e) => handleKeyDown(i, e)}
      onPaste={handlePaste}
      onFocus={handleFocus}
      disabled={disabled}
      $error={error}
      $fullWidth={fullWidth}
      aria-label={`Digit ${i + 1} of ${length}`}
    />
  );

  return (
    <StyledContainer data-otp-input $fullWidth={fullWidth}>
      {groups.map((group, groupIndex) => (
        <Fragment key={groupIndex}>
          {groupIndex > 0 && <StyledDivider />}
          <StyledGroup $fullWidth={fullWidth}>{group.map(renderCell)}</StyledGroup>
        </Fragment>
      ))}
    </StyledContainer>
  );
});

export default OtpInput;
export type { OtpInputHandle } from './types';

OtpInput.displayName = 'OtpInput';
